import React, { useRef, useEffect } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import Link from "next/link";
import { NextSeo } from "next-seo";
import * as Text from "@/components/text";
import Award from "@/components/film/Award";
import { Disclosure, Transition } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/outline";
import Metadata from "@/components/film/Metadata";
import Button from "@/components/actions/Button";
import { Nav } from "@/components/navigation/Navbar";
import apiClient from "@/utils/apiClient";
import { getStrapiImage } from "@/utils/strapiFunctions";
import ReactMarkdown from "react-markdown";

import { motion, AnimatePresence } from "framer-motion";
import qs from "qs";

import { VerticalCard, HorizontalCard } from "@/components/publications/Cards";

import { MovieBanner } from "@/components/banners/TourBanner";
import InitiativeCard from "@/components/banners/InitiativeCard";
import RelatedMovies from "@/components/movies/RelatedMovies";
import classNames from "classnames";
import breakpoints from "@/constants/breakpoinst";
import useWindowSize from "@/hooks/useWindowSize";
import Trailer from "@/components/content/Trailer";

import RelatedContent from "@/components/movies/RelatedContent";
import { localeTexts } from '@/utils/locale';


export default function Documental({ movie, locale }) {
  const router = useRouter();
  const windowSize = useWindowSize();
  const heroRef = useRef(null);
  const detailsRef = useRef(null);
  const projectionsRef = useRef(null);
  const eventsRef = useRef(null);
  const similarMovies = useRef(null);
  const relatedRef = useRef(null);
  const [smallHero, setSmallHero] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [movieData, setMovieData] = React.useState({...movie.attributes});


  const creditFieldsMap = {
    _amb_fotografia: "Fotografía",
    _amb_guion: "Guión",
    _amb_edicion: "Edición",
    _amb_sonido: "Sonido",
    _amb_musica: "Música",
    _amb_animacion: "Animación",
    _amb_diseno_sonoro: "Diseño sonoro",
    _amb_produccion: "Producción",
    _amb_produccion_ejecutiva: "Producción ejecutiva",
    _amb_compania_productora: "Compañía productora",
  };

  const container = {
    initial: { y: -200, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      duration: 10,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
    exit: { y: 100, opacity: 1 },
  };
   useEffect(() => {
    if (locale === "en" && movie.attributes.localizations?.data?.length > 0) {
      // Find the English localization
      const englishData = movie.attributes.localizations.data.find(
        (localization) => localization.attributes.locale === "en"
      );

      if (englishData) {
        setMovieData({
          ...movie.attributes, // Preserve other attributes
          ...englishData.attributes, // Override with English attributes
        });
      }
    } else {
      // Reset to the default locale (original attributes)
      setMovieData({ ...movie.attributes });
    }
  }, [locale, movie.attributes]);

  const topRef = useRef(null);
  useEffect(() => {
    const handlePopstate = () => {
      window.location.reload();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, []);

  return (
    <>
      <NextSeo
        title={movieData.film_name}
        description={movieData.film_synopsis}
        canonical={`https://ambulante.org/blog/${movieData.filmSlug}`}
        openGraph={{
          url: `https://ambulante.org/blog/${movieData.filmSlug}`,
          title: movieData.film_name,
          description: movieData.film_synopsis || "",
          images: [
            {
              url:
                getStrapiImage(movieData.film_cover_image, "large") ||
                "",
              width: 800,
              height: 600,
              alt: movieData.film_name,
            },
          ],
          site_name: "Ambulante",
        }}
      />
      <div ref={topRef} className="sticky top-0 z-1 ">
        <div className="bg-neutral-50 py-3 ">
          <div className="amb-container">
            <ul className="flex items-center gap-5">
              <li className="flex space-x-2">
                <Text.Link
                  label="← Atrás"
                  onClick={() => router.back()}
                  size="md"
                  className="hidden flex-none items-center align-middle text-neutral-1000 md:flex"
                ></Text.Link>
              </li>
              <li className="flex after:ml-5 after:content-['/'] ">
                <Link href="/">
                  <Text.Link
                    className="text-neutral-1000"
                    size="md"
                    label="Inicio"
                  ></Text.Link>
                </Link>
              </li>
              <li className="flex after:ml-5 after:content-['/'] ">
                <Link href="/iniciativas/gira?seccion=las-peliculas">
                  <Text.Link
                    className="text-neutral-1000"
                    size="md"
                    label="Pelīculas"
                  ></Text.Link>
                </Link>
              </li>
              <li>
                <Text.Body
                  className="line-clamp-1 overflow-hidden text-ellipsis break-normal font-light text-neutral-500"
                  size="lg"
                ></Text.Body>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="amb-container hidden py-10 lg:block">
        <Text.Display as="h1" size="lg">
          {movieData.film_name}
        </Text.Display>
        {movieData.film_original_title ? (
          <Text.Display as="h2" size="sm" fontWeight="light">
            ({movieData.film_original_title})
          </Text.Display>
        ) : null}
      </div>
      <div className="amb-container py-10  lg:hidden">
        <Text.Display as="h1" size="sm">
          {movieData.film_name}
        </Text.Display>
        {movieData.film_original_title ? (
          <Text.Headline as="h2" size="sm" fontWeight="light">
            ({movieData.film_original_title})
          </Text.Headline>
        ) : null}
      </div>

      {movieData.film_cover_image?.data?.attributes ? (
        <div>
          <img
            className="relative h-[320px] w-full object-cover lg:h-[526px]  "
            alt=""
            src={getStrapiImage(movieData.film_cover_image)}
          />
        </div>
      ) : null}

      <div className=" top-0 z-50 border-b border-black  ">
        <div className="amb-container flex">
          <div className="flex-grow"></div>
          <div className="flex">
            {movieData.film_trailer_link ? (
              <Link href={movieData.film_trailer_link}>
                <Button
                  leadingIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0118 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0118 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 016 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M19.125 12h1.5m0 0c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h1.5m14.25 0h1.5"
                      />
                    </svg>
                  }
                  className="flex h-[100%] w-full border-none"
                  color="terciary-light"
                  label={localeTexts[locale].vtrailer}
                ></Button>
              </Link>
            ) : null}
            {movieData.film_stream_link ? (
              <Link href={movieData.film_stream_link}>
                <Button
                  leadingIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"
                      />
                    </svg>
                  }
                  className="flex h-[100%] w-full border-none"
                  color="secondary-light"
                  label="Ver en linea"
                ></Button>
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      <div className="amb-container grid-cols-5 gap-10 lg:my-10 lg:grid">
        <div className="mb-10 flex flex-col-reverse space-y-7 lg:mb-0 lg:block">
          <div className="px-5">
            <Text.Overline className="text-secondary-light">
              {localeTexts[locale].direccion}
            </Text.Overline>
            <ul className="mt-2">
              {movieData.Directores.map((director) => (
                <Text.Body as="li" size="lg" key={director.id}>
                  {director.Director}
                </Text.Body>
              ))}
            </ul>
          </div>

          <div className="space-y-5 bg-neutral-50 px-5 py-3 text-center lg:text-left">
            {movieData.film_length > 0 ? (
              <div>
                <Text.Overline className="text-secondary-light">
                  {localeTexts[locale].duracion}
                </Text.Overline>
                <ul>
                  <Text.Body as="li" size="lg">
                    {movieData.film_length} min
                  </Text.Body>
                </ul>
              </div>
            ) : null}

            {movieData.countries.length > 0 ? (
              <div>
                <Text.Overline className="text-secondary-light">
                  {localeTexts[locale].paises}
                </Text.Overline>
                <ul>
                  {movieData.countries.map((country) => (
                    <Text.Body as="li" size="lg" key={country.id}>
                      {country.countries}
                    </Text.Body>
                  ))}
                </ul>
              </div>
            ) : null}

            {movieData.film_year.length > 0 ? (
              <div>
                <Text.Overline className="text-secondary-light">
                  {localeTexts[locale].ano}
                </Text.Overline>
                <ul>
                  {movieData.film_year.map((year, i) => (
                    <>
                      {i > 0
                        ? movieData.film_individual_years
                          ? "/"
                          : " - "
                        : null}
                      <Text.Body as="span" size="lg" key={year.id}>
                        {year.year}
                      </Text.Body>
                    </>
                  ))}
                </ul>
              </div>
            ) : null}

            {movieData.idiomas.data.length > 0 ? (
              <div>
                <Text.Overline className="text-secondary-light">
                  {localeTexts[locale].idiomas}
                </Text.Overline>
                <ul>
                  {movieData.idiomas.data.map((idioma) => (
                    <Text.Body as="li" size="lg" key={idioma.id}>
                      {idioma.attributes.name}
                    </Text.Body>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
        <div className="col-span-3 space-y-10">
          {movieData.film_synopsis ? (
            <div>
              <Text.Headline size="sm">{localeTexts[locale].sinopsis}</Text.Headline>
              <ReactMarkdown className="font-body prose mt-5 prose-h2:uppercase lg:max-w-[100ch]">
                {movieData.film_synopsis}
              </ReactMarkdown>
            </div>
          ) : null}

          {movieData.film_semblanza ? (
            <div>
              <Text.Headline size="sm">{localeTexts[locale].semblanza}</Text.Headline>
              <ReactMarkdown className="font-body prose mt-5 prose-h2:uppercase lg:max-w-[100ch]">
                {movieData.film_semblanza}
              </ReactMarkdown>
            </div>
          ) : null}

          {/* {movieData.content ? <div>

            <ReactMarkdown className="font-body prose mt-10 prose-h2:uppercase lg:max-w-[100ch]">
              {movieData.content}
            </ReactMarkdown>
          </div> : null} */}

          {movieData.awards.length > 0 ? (
            <div className="space-y-5">
              <Text.Headline as="h3" size="sm" className="mb-10">
                {localeTexts[locale].festivales}
              </Text.Headline>
              <div className="flex flex-wrap gap-5">
                {movieData.awards.map((award) => (
                  <Award key={award.id} size="sm" title={award.name} />
                ))}
              </div>
            </div>
          ) : null}
          {movie.attributes.aliados.data.length > 0 && (
            <div className="space-y-5">
              <Text.Headline as="h3" size="sm" className="mb-10">
                Aliados
              </Text.Headline>
              <div className="grid-cols-2 gap-4 md:grid md:grid-cols-3  ">
                {movie.attributes.aliados.data.map((aliado) => (
                  <div
                    key={aliado.id}
                    className="flex min-h-[100px] items-center justify-center p-2  md:p-4"
                  >
                    {aliado.attributes.logo?.data?.attributes?.url && (
                      <div
                        className="h-[100px] w-full"
                        key={aliado.attributes.name}
                      >
                        <a
                          className="flex h-full items-center align-middle"
                          href={aliado.attributes.website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <img
                            src={getStrapiImage(aliado.attributes.logo)}
                            alt={aliado.attributes.name}
                            className="m-auto h-1/2 h-full w-full object-contain"
                          />
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-5">
          {movieData.temas.data.length > 0 ? (
            <div>
              <Text.Overline size="md" className="text-neutral-600">
                {localeTexts[locale].tematicas}
              </Text.Overline>
              <ul className="mt-2 flex flex-wrap gap-x-2 gap-y-2">
                {movieData.temas.data.map((topic, i) => (
                  <li
                    key={topic.id}
                    className="flex flex-none items-center rounded-full bg-neutral-100 px-3"
                  >
                    <Text.Body className="text-neutral-900" size="lg">
                      &nbsp;
                      {topic.attributes.name}&nbsp;
                    </Text.Body>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div>
            {movieData.iniciativas.data.length > 0 ? (
              <div>
                <Text.Overline size="md" className="text-neutral-600">
                  {localeTexts[locale].iniciativas}
                </Text.Overline>
                <ul className="mt-2 space-y-2 ">
                  {movieData.iniciativas.data.map((initiative, i) => (
                    <li key={initiative.id} className=" px-3 ">
                      <div
                        className={classNames(
                          {
                            "ambulante presenta": "bg-presenta",
                            ediciones: "bg-ediciones",
                            "gira ambulante": "bg-gira",
                            "mas-alla": "bg-mas-alla",
                          }[initiative.attributes.name.toLowerCase()],
                          "inline-block h-3 w-3 rounded-full"
                        )}
                      ></div>
                      <Text.Body
                        className="ml-2 inline-block border-b-2 border-neutral-300 text-neutral-900"
                        size="lg"
                      >
                        &nbsp;
                        {initiative.attributes.name}&nbsp;
                      </Text.Body>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <div>
            {movieData.descargas?.length > 0 ? (
              <div>
                <Text.Overline size="md" className="text-neutral-600">
                  {localeTexts[locale].descargas}
                </Text.Overline>
                <ul className="mt-2 space-y-2 ">
                  {movieData.descargas.map((download, i) => (
                    <Link target="_blank" key={download.id} href={download.url}>
                      <Text.Link size="lg" label={download.label} />
                    </Link>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {movieData.credits.length > 0 ? (
        <div className="py-5 ">
          <Disclosure as="div" className="overflow-hidden">
            {({ open }) => (
              <>
                <Disclosure.Button
                  className={`${open
                      ? "bg-neutral-1000 text-white hover:bg-neutral-800"
                      : "hover:bg-neutral-50"
                    } border-neutrl-1000 flex w-[100%] items-center justify-center border-y-2 py-5 align-middle `}
                >
                  <div className="flex">
                  <Text.Label size="xl" fontWeight="bold">
                    {!open ? (
                      localeTexts[locale].Mcreditos) : (localeTexts[locale].Ocreditos)}
                  </Text.Label>
                    <motion.div
                      className="block px-1"
                      animate={{ rotateZ: open ? 0 : 180 }}
                    >
                      <ChevronUpIcon
                        className={`${open ? "text-white " : " text-gray-500"
                          }h-5 ml-3 inline-flex w-5`}
                      />
                    </motion.div>
                  </div>
                </Disclosure.Button>

                <Disclosure.Panel
                  as={motion.div}
                  variants={container}
                  animate="animate"
                  initial="initial"
                  exit="exit"
                  className="relative  -top-2 bg-black text-white"
                >
                  <motion.div
                    variants={container}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="amb-container "
                  >
                    <AnimatePresence exitBeforeEnter onExitComplete={true}>
                      <motion.ul
                        variants={container}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className="divide-y divide-white/10 border-t-2 border-white/10"
                      >
                        {movieData.credits.map((credit, i) => (
                          <motion.li
                            variants={container}
                            key={i}
                            className="grid grid-cols-5 py-5"
                          >
                            <Text.Label className="col-span-2 uppercase text-neutral-500">
                              {credit.role}
                            </Text.Label>
                            <Text.Body
                              className="col-span-3 text-neutral-100"
                              size="xl"
                            >
                              {credit.name}
                            </Text.Body>
                          </motion.li>
                        ))}
                      </motion.ul>
                    </AnimatePresence>
                  </motion.div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      ) : null}
      <div className="amb-container">
        <RelatedContent movieId={movie.id} />
      </div>
    </>
  );
}

export async function getServerSideProps(context) {
  try {
    const { id: slug } = context.params;
    const {locale} = context.query;
    const query = qs.stringify(
      {
        filters: {
          filmSlug: {
            $eq: slug,
          },
        },

        populate: {
          localizations: "*",
          Directores: "*",
          film_cover_image: "*",
          countries: "*",
          film_year: "*",
          idiomas: "*",
          awards: "*",
          aliados: {
            populate: "logo",
          },
          temas: "*",
          iniciativas: "*",
          credits: "*",
          descargas: "*",
        },
      },
      {
        encodeValuesOnly: true, // prettify URL
      }
    );

    const movieResponse = await apiClient.get(`/api/films?${query}`);
    return {
      props: {
        movie: movieResponse.data.data[0],
        locale: locale || "es-MX",
      },
    };
  } catch (err) {
    console.log(err);
    return {
      notFound: true,
    };
  }
}

Documental.getLayout = function getLayout(page) {
  return (
    <div>
      <div className="amb-container bg-white">
        <Nav theme="light" />
      </div>
      <div>{page}</div>
    </div>
  );
};
